import {message, Modal} from "antd";
import {ApolloError} from "@apollo/client";
import {erasedRecordMessage} from "../helpers/erasedRecordMessage";
import {PAYMENT_METHOD_NOT_RELEASED_TITLE, paymentMethodNotReleasedMessage} from "./paymentMethodNotReleased";

export type KnownError = {
    id?: string | number;
    key: string;
    title: string;
    msg: string;
    additionalInfo?: {[key: string]: string | number};
    useOriginalMessage?: boolean;
};
export type ErrorType = "modal" | "message" | "console";

export const handleError = async (context: string, error: unknown, type?: ErrorType, knownErrors?: Array<KnownError>, dataTestId?: string) => {
    const errorAs = type ?? "modal";

    document.documentElement.setAttribute("error-occurred", "true");

    // Sperre nach DSGVO-Löschantrag: die Guard-Meldung des Servers sagt, was nicht geht. Kein Aufrufer
    // führt sie in seinen knownErrors, sie fiele sonst auf „Unbekannter Fehler“.
    const erasedMessage = erasedRecordMessage(error);
    if (erasedMessage) {
        renderError({key: "erasedRecord", title: "Nicht möglich", msg: erasedMessage}, errorAs);

        return;
    }

    // #1008: Mitarbeiter-Sperre für Bezahlmethoden — am Code erkannt, damit jede Maske mit
    // handleError die Server-Meldung zeigt, ohne eigenen knownErrors-Eintrag.
    const notReleasedMessage = paymentMethodNotReleasedMessage(error);
    if (notReleasedMessage) {
        renderError({key: "paymentMethodNotReleased", title: PAYMENT_METHOD_NOT_RELEASED_TITLE, msg: notReleasedMessage}, errorAs);

        return;
    }

    if (error instanceof ApolloError && knownErrors) {
        const knownErrorsArray = knownErrors.map((e) => e.key);
        const errorMessage = error.graphQLErrors?.[0]?.message;

        if (!errorMessage) {
            // Bei HTTP-Fehlern (z.B. 413 Payload Too Large) gibt es keine graphQLErrors,
            // aber networkError.response.statusText oder error.message können gegen knownErrors matchen
            const networkError = error.networkError as (Error & {response?: Response; statusCode?: number}) | null;
            const candidates: string[] = [];
            if (networkError?.response?.statusText) {
                candidates.push(networkError.response.statusText);
            }
            if (networkError?.message) {
                candidates.push(networkError.message);
            }
            if (error.message) {
                candidates.push(error.message);
            }
            const foundNetworkError = knownErrors.find((e) =>
                candidates.some((c) => c.includes(e.key))
            );
            if (foundNetworkError) {
                renderError(foundNetworkError, errorAs);
                return;
            }

            const fallBackError: KnownError = {
                key: "default",
                title: "Unbekannter Fehler",
                msg: error.message || "Es ist ein Fehler aufgetreten.",
            };
            renderError(fallBackError, errorAs);
            return;
        }

        if (knownErrorsArray.some((e) => errorMessage.includes(e))) {
            const foundError = knownErrors.find((e) => errorMessage.includes(e.key));

            if (foundError?.useOriginalMessage) {
                const cleanedMessage = errorMessage.replace(/^.*Error:\s*/, "");
                renderError({...foundError, msg: cleanedMessage || errorMessage}, errorAs);
            } else {
                renderError(foundError, errorAs);
            }
        } else if (knownErrorsArray.some((e) => errorMessage.includes("default"))) {
            const foundError = knownErrors.find((e) => errorMessage.includes("default"));

            renderError(foundError, errorAs);
        } else {
            const fallBackError: KnownError = {
                key: "default",
                title: "Unbekannter Fehler",
                msg: "Es ist ein unbekannter Fehler aufgetreten.",
            };

            renderError(fallBackError, errorAs);
        }
    }
};

const renderError = (error: KnownError | undefined, errorAs: ErrorType, dataTestId?: string) => {
    if (error) {
        switch (errorAs) {
            case "modal":
                Modal.confirm({
                    title: error.title,
                    content: error.msg,
                    cancelButtonProps: {style: {display: "none"}},
                    okButtonProps: {block: true},
                });
                break;
            case "message":
                message.error({
                    content: `${error.title}: \n ${error.msg}`,
                    style: {whiteSpace: "pre-line"},
                    duration: 7,
                    onClick: () => {
                        message.destroy();
                    },
                });
                break;
            case "console":
                console.log(error.msg);
                break;
            default:
                break;
        }
    }
};
